import React, {FC, ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

import {ProductCardSpec, iconPaths, useProductCards} from '../../data';

/**
 * 五大 AI 服务：2 + 3 两行产品卡。
 * 卡片 1（跨境数字化营销）设计稿即无链接，渲染为 div；其余按 to（站内 Link）/ href（站外 a）渲染。
 */

const Products: FC = () => {
  const {t} = useTranslation('home');
  const productCards = useProductCards();

  const renderCardBody = (card: ProductCardSpec): ReactNode => (
    <>
      <div className="h-56 overflow-hidden">
        <img
          alt={card.imageAlt}
          className="object-cover object-top w-full h-full group-hover:scale-105 transition-transform duration-500"
          decoding="async"
          loading="lazy"
          src={card.imageSrc}
        />
      </div>
      <div className="p-8">
        {/* icon 与标题+介绍同行，压缩纵向占用，把高度让给头图 */}
        <div className="flex items-start gap-4 mb-4">
          <div
            className={`w-14 h-14 rounded-lg flex items-center justify-center shrink-0 transition-colors ${card.iconBgClass} ${card.iconHoverBgClass}`}
          >
            <svg
              className={`w-7 h-7 ${card.iconColorClass}`}
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              viewBox="0 0 24 24"
            >
              {card.iconPaths.map((d) => (
                <path key={d} d={d} strokeLinecap="round" strokeLinejoin="round" />
              ))}
            </svg>
          </div>
          <div>
            <h3 className="font-display font-bold text-xl text-slate-900 mb-1">{card.title}</h3>
            <p className="text-slate-500 text-sm leading-relaxed">{card.description}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {card.tags.map((tag) => (
            <span className="tag-pill text-xs px-3 py-1 rounded-md" key={tag}>
              {tag}
            </span>
          ))}
        </div>
        <div
          className={`mt-6 flex items-center gap-2 text-sm font-medium opacity-100 group-hover:opacity-100 transition-opacity ${card.iconColorClass}`}
        >
          {t('products.learnMore')}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
            <path d={iconPaths.chevronRight} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
    </>
  );

  const renderCard = (card: ProductCardSpec): ReactNode => {
    const shellClass = `product-card glass-card rounded-lg overflow-hidden scroll-reveal group cursor-pointer shadow-[0_10px_30px_rgba(0,0,0,0.06)]`;
    const shellStyle = {transitionDelay: card.transitionDelay};
    if (card.to) {
      return (
        <Link className={shellClass} key={card.key} style={shellStyle} to={card.to}>
          {renderCardBody(card)}
        </Link>
      );
    }
    if (card.href) {
      return (
        <a className={shellClass} href={card.href} key={card.key} rel="noreferrer" style={shellStyle} target="_blank">
          {renderCardBody(card)}
        </a>
      );
    }
    return (
      <div className={shellClass} key={card.key} style={shellStyle}>
        {renderCardBody(card)}
      </div>
    );
  };

  return (
    <section className="relative py-24 lg:py-32 bg-white">
      <div className="max-w-[90rem] mx-auto px-6 lg:px-8">
        <div className="text-center mb-16 scroll-reveal">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-md glass-card mb-6">
            <span className="text-sm text-indigo-600 font-medium">{t('products.badge')}</span>
          </div>
          <h2 className="font-display font-bold text-3xl lg:text-5xl text-slate-900 mb-4">
            {t('products.title1')}
            <span className="text-gradient">{t('products.title2')}</span>
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">{t('products.subtitle')}</p>
        </div>

        <div className="grid gap-6 lg:gap-8">
          {/* 第一行 2 列 */}
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">{productCards.slice(0, 2).map(renderCard)}</div>
          {/* 第二行 3 列 */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">{productCards.slice(2, 5).map(renderCard)}</div>
        </div>
      </div>
    </section>
  );
};

export default Products;
